/*
  File: TeamPage.tsx
  Path: src/features/dashboard/pages/TeamPage.tsx
  Description:
	P10 Team roster page.
	Real operator-scoped staff roster from /api/v1/dashboard/team.
	Team owns roster. Schedule owns assignment. Ops Board consumes assignment.

  Version: 0.6.1
  Status: Active

  Change Log:
	- 2026-06-20 - v0.1.0:
	  - Add real Team page shell consuming backend team truth.
	- 2026-06-20 - v0.2.0:
	  - Replace scaffold with approved Team UI direction.
	  - Add cards/list toggle, stat strip, filters, search, and presentational add-team modal.
	  - Keep backend /api/v1/dashboard/team as source of truth.
	- 2026-06-20 - v0.3.0:
	  - Move Team page styling to TeamPage.css.
	  - Add language chips and Edit profile entry points.
	  - Keep Add/Edit as centered Peek card shell pending backend POST/PATCH/archive.
	- 2026-06-20 - v0.4.0:
	  - Realign Team page structure to approved prototype.
	  - Move Edit profile to card footer beside language chips.
	  - Remove duplicated agency rendering.
	  - Preserve backend-derived assignability and operator-scoped API truth.
	- 2026-06-20 - v0.4.1:
	  - Add modal structural class hooks; clock icons deferred pending design-team icon source.
	  - Prepare Team Add/Edit Peek card for CSS parity pass.
	- 2026-07-26 - v0.5.0:
	  - Wire Add (POST), Edit (PATCH), and Archive (POST) to /api/v1/dashboard/team.
	  - Add email field on Add; full name is read-only on Edit (identity is the anchor).
	  - Roster reloads from backend truth after any save; assignability stays backend-derived.
	- 2026-07-26 - v0.6.0: Add two-step archive confirmation and distinct Archive styling.
	- 2026-08-17 - v0.6.1: LSM (larry) Added prototyp for Local Pro Association #
*/

import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../utils/api";
import "./TeamPage.css";

type ViewMode = "cards" | "list";
type FilterMode = "all" | "guides" | "instructors" | "assignable" | "attention";
type RoleMode = "guide" | "instructor";

type TeamMember = {
  leader_id: number | string;
  user_id: number | string;
  operator_id: number | string | null;
  full_name?: string | null;
  display_name?: string | null;
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  role?: string | null;
  certification_level?: string | null;
  agency?: string | null;
  member_number?: string | null;
  membership_expiry?: string | null;
  local_professional_authority?: string | null;
  local_professional_category?: string | null;
  local_professional_id_number?: string | null;
  local_professional_expiry?: string | null;
  is_insured?: boolean | null;
  insurance_carrier?: string | null;
  insurance_policy_no?: string | null;
  insurance_expiry?: string | null;
  efr_expiry?: string | null;
  languages_spoken?: string | null;
  years_experience?: number | string | null;
  profile_photo_url?: string | null;
  bio?: string | null;
  is_active?: boolean | null;
  archived_at?: string | null;
  is_assignable?: boolean | null;
  team_status?: string | null;
  team_status_reason?: string | null;
  membership_expiring_soon?: boolean | null;
  insurance_expiring_soon?: boolean | null;
  efr_expiring_soon?: boolean | null;
};

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ok"; team: TeamMember[] };

function labelize(value?: string | null): string {
  if (!value) return "—";
  return String(value).replace(/_/g, " ");
}

function lower(value?: string | null): string {
  return String(value || "").toLowerCase();
}

function fmtDate(value?: string | null): string {
  if (!value) return "—";
  return String(value).slice(0, 10);
}

function fmtShortDate(value?: string | null): string {
  const iso = fmtDate(value);
  if (iso === "—") return "—";
  const parts = iso.split("-");
  if (parts.length !== 3) return iso;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const monthIndex = Number(parts[1]) - 1;
  return `${parts[2]} ${months[monthIndex] || parts[1]} ${parts[0].slice(2)}`;
}

function memberName(member?: TeamMember): string {
  if (!member) return "";
  const composed = `${member.first_name || ""} ${member.last_name || ""}`.trim();
  return member.full_name || member.display_name || member.name || composed || `Leader #${member.leader_id}`;
}

function initials(name: string): string {
  return (
	name
	  .split(" ")
	  .filter(Boolean)
	  .map((p) => p[0])
	  .join("")
	  .slice(0, 2)
	  .toUpperCase() || "TM"
  );
}

function isGuide(member: TeamMember): boolean {
  const role = lower(member.role);
  const cert = lower(member.certification_level);
  return role.includes("guide") || cert.includes("guide") || cert.includes("divemaster") || cert.includes("dive master");
}

function isInstructor(member: TeamMember): boolean {
  const role = lower(member.role);
  const cert = lower(member.certification_level);
  return role.includes("instructor") || cert.includes("instructor") || cert.includes("owsi") || cert.includes("idc");
}

function roleLabel(member: TeamMember): string {
  if (isInstructor(member)) return "Instructor";
  if (isGuide(member)) return "Guide";
  return labelize(member.role);
}

function languageList(value?: string | null): string[] {
  return String(value || "")
	.split(",")
	.map((lang) => lang.trim())
	.filter(Boolean)
	.map((lang) => lang.slice(0, 2).toUpperCase());
}

function certLine(member: TeamMember): string {
  const cert = member.certification_level || "Certification TBD";
  const agency = member.agency || "";

  if (agency && cert.toLowerCase().startsWith(agency.toLowerCase())) {
	return member.member_number ? `${cert} · #${member.member_number}` : cert;
  }

  const line = agency ? `${agency} ${cert}` : cert;
  return member.member_number ? `${line} · #${member.member_number}` : line;
}

function locationLine(member: TeamMember): string {
  if (!member.years_experience) return "";
  return `${member.years_experience} yrs`;
}

function localProfessionalLine(member: TeamMember): string {
  const authority = member.local_professional_authority?.trim();
  const category = member.local_professional_category?.trim();
  const number = member.local_professional_id_number?.trim();
  const expiry = member.local_professional_expiry ? fmtShortDate(member.local_professional_expiry) : "";

  const parts = [
    authority,
    category,
    number ? `#${number}` : "",
    expiry ? `exp ${expiry}` : "",
  ].filter((part): part is string => Boolean(part && part.trim()));

  return parts.length > 0 ? parts.join(" · ") : "";
}

function compactBio(member: TeamMember, maxLength = 150): string {
  const bio = String(member.bio || "").trim();
  if (!bio) return "";
  return bio.length > maxLength ? `${bio.slice(0, maxLength - 1).trim()}…` : bio;
}

function MemberAvatar({ member, name, className }: { member?: TeamMember; name: string; className: string }) {
  const photoUrl = String(member?.profile_photo_url || "").trim();

  return (
    <div className={className}>
      {photoUrl ? <img src={photoUrl} alt={name ? `${name} profile` : "Team member profile"} /> : initials(name)}
    </div>
  );
}

export default function TeamPage() {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [viewMode, setViewMode] = useState<ViewMode>("cards");
  const [filterMode, setFilterMode] = useState<FilterMode>("all");
  const [search, setSearch] = useState("");
  const [activeModal, setActiveModal] = useState<{ mode: "add" | "edit"; member?: TeamMember } | null>(null);
  const [rosterReloadKey, setRosterReloadKey] = useState(0);

  useEffect(() => {
	let mounted = true;

	async function loadTeam() {
	  try {
		const response = await apiFetch("/api/v1/dashboard/team", { method: "GET" });
		const json = await response.json().catch(() => ({}));

		if (!response.ok || json?.ok === false) {
		  throw new Error(json?.message || `HTTP ${response.status}`);
		}

		if (mounted) {
		  setState({ status: "ok", team: Array.isArray(json?.team) ? json.team : [] });
		}
	  } catch (err: any) {
		if (mounted) setState({ status: "error", message: err?.message || "Team load failed." });
	  }
	}

	loadTeam();

	return () => {
	  mounted = false;
	};
  }, [rosterReloadKey]);

  const team = useMemo(() => (state.status === "ok" ? state.team : []), [state]);

  const stats = useMemo(() => ({
	total: team.length,
	assignable: team.filter((m) => m.is_assignable).length,
	guides: team.filter(isGuide).length,
	instructors: team.filter(isInstructor).length,
	attention: team.filter((m) => !m.is_assignable).length,
	expiring: team.filter((m) => m.membership_expiring_soon || m.insurance_expiring_soon || m.efr_expiring_soon).length,
  }), [team]);

  const visibleTeam = useMemo(() => {
	const q = search.trim().toLowerCase();

	return team.filter((member) => {
	  const name = memberName(member).toLowerCase();
	  const role = lower(member.role);
	  const cert = lower(member.certification_level);
	  const agency = lower(member.agency);
	  const number = lower(member.member_number);
	  const localPro = [
		member.local_professional_authority,
		member.local_professional_category,
		member.local_professional_id_number,
		member.local_professional_expiry,
	  ].map((value) => lower(value)).join(" ");

	  if (q && !`${name} ${role} ${cert} ${agency} ${number} ${localPro}`.includes(q)) return false;
	  if (filterMode === "guides") return isGuide(member);
	  if (filterMode === "instructors") return isInstructor(member);
	  if (filterMode === "assignable") return Boolean(member.is_assignable);
	  if (filterMode === "attention") return !member.is_assignable;
	  return true;
	});
  }, [team, search, filterMode]);

  if (state.status === "loading") {
	return (
	  <Shell>
		<div className="team-card">Loading team roster…</div>
	  </Shell>
	);
  }

  if (state.status === "error") {
	return (
	  <Shell>
		<div className="team-error">Failed to load team: {state.message}</div>
	  </Shell>
	);
  }

  return (
	<Shell>
	  <section className="team-crown">
		<div>
		  <h1 className="team-title">Team</h1>
		  <p className="team-subtitle">
			Your dive staff — who works here, what they are qualified to run, and whether their professional currency is in date.
			This roster feeds guide and instructor assignment on Schedule.
		  </p>
		</div>
		<button className="team-primary-btn" onClick={() => setActiveModal({ mode: "add" })}>
		  + Add team member
		</button>
	  </section>

	  <section className="team-stats">
		<Stat label="Assignable Now" value={stats.assignable} tone="assignable" />
		<Stat label="Total Staff" value={stats.total} />
		<Stat label="Expiring < 30 Days" value={stats.expiring} tone="expiring" />
		<Stat label="Lapsed · Not Assignable" value={stats.attention} tone="attention" />
	  </section>

	  <section className="team-toolbar">
		<div className="team-filters">
		  <Filter label={`All ${stats.total}`} active={filterMode === "all"} onClick={() => setFilterMode("all")} />
		  <Filter label={`Guides ${stats.guides}`} active={filterMode === "guides"} onClick={() => setFilterMode("guides")} />
		  <Filter label={`Instructors ${stats.instructors}`} active={filterMode === "instructors"} onClick={() => setFilterMode("instructors")} />
		  <Filter label={`Assignable ${stats.assignable}`} active={filterMode === "assignable"} onClick={() => setFilterMode("assignable")} />
		  <Filter label={`Needs attention ${stats.attention}`} active={filterMode === "attention"} onClick={() => setFilterMode("attention")} />
		</div>

		<div className="team-right-tools">
		  <span className="team-view-toggle">
			<button className={viewMode === "cards" ? "is-active" : ""} onClick={() => setViewMode("cards")}>
			  Cards
			</button>
			<button className={viewMode === "list" ? "is-active" : ""} onClick={() => setViewMode("list")}>
			  List
			</button>
		  </span>
		  <input className="team-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search team…" />
		</div>
	  </section>

	  {visibleTeam.length === 0 ? (
		<div className="team-card">No matching team members.</div>
	  ) : viewMode === "cards" ? (
		<section className="team-grid">
		  {visibleTeam.map((member) => (
			<TeamCard key={String(member.leader_id)} member={member} onEdit={() => setActiveModal({ mode: "edit", member })} />
		  ))}
		</section>
	  ) : (
		<section className="team-list">
		  {visibleTeam.map((member) => (
			<TeamRow key={String(member.leader_id)} member={member} onEdit={() => setActiveModal({ mode: "edit", member })} />
		  ))}
		</section>
	  )}

	  <p className="team-note">
		<strong>Assignable</strong> is derived, never typed: a member is assignable only when active, membership is current,
		insurance is current, and EFR/CPR is in date. <strong>Three clocks:</strong> Membership · Insurance · EFR.
		<strong> Archive, never delete</strong>.
	  </p>

	  {activeModal ? (
		<AddTeamModal
		  mode={activeModal.mode}
		  member={activeModal.member}
		  onClose={() => setActiveModal(null)}
		  onSaved={() => {
			setActiveModal(null);
			setRosterReloadKey((value) => value + 1);
		  }}
		/>
	  ) : null}
	</Shell>
  );
}

function Shell(props: { children: React.ReactNode }) {
  return (
	<div className="team-page">
	  <div className="team-kicker">Operations</div>
	  {props.children}
	</div>
  );
}

function Stat(props: { label: string; value: number; tone?: "assignable" | "expiring" | "attention" }) {
  const toneClass = props.tone ? ` team-stat-${props.tone}` : "";
  return (
	<div className={`team-stat${toneClass}`}>
	  <div className="team-stat-label">{props.label}</div>
	  <div className="team-stat-value">{props.value}</div>
	</div>
  );
}

function Filter(props: { label: string; active: boolean; onClick: () => void }) {
  return (
	<button className={props.active ? "team-filter is-active" : "team-filter"} onClick={props.onClick}>
	  {props.label}
	</button>
  );
}

function TeamCard({ member, onEdit }: { member: TeamMember; onEdit: () => void }) {
  const name = memberName(member);
  const languages = languageList(member.languages_spoken);
  const localProLine = localProfessionalLine(member);
  const bioLine = compactBio(member);

  return (
	<article className={member.is_assignable ? "team-member-card" : "team-member-card team-member-card-attention"}>
	  <div className="team-card-head">
		<MemberAvatar member={member} name={name} className="team-avatar" />

		<div className="team-member-identity">
		  <div className="team-member-name">{name}</div>
		  <span className={isInstructor(member) ? "team-role team-role-instructor" : "team-role team-role-guide"}>
			{roleLabel(member)}
		  </span>
		  <div className="team-member-meta">
			<strong>{certLine(member)}</strong>
			{locationLine(member) ? (
			  <>
				<br />
				{locationLine(member)}
			  </>
			) : null}
			{localProLine ? (
			  <>
				<br />
				<strong>Local pro:</strong> {localProLine}
			  </>
			) : null}
		  </div>
		</div>

		<StatusPill member={member} />
	  </div>

	  {bioLine ? <p className="team-member-bio">{bioLine}</p> : null}

	  <div className="team-clocks">
		<Clock label="Membership" status={member.membership_expiring_soon ? "Renew soon" : "Current"} detail={`exp ${fmtShortDate(member.membership_expiry)}`} tone={member.team_status_reason === "membership_expired" ? "bad" : member.membership_expiring_soon ? "warn" : "ok"} />
		<Clock label="Insurance" status={member.is_insured ? "Insured" : "Not insured"} detail={member.is_insured ? `${member.insurance_carrier || "Carrier"} · exp ${fmtShortDate(member.insurance_expiry)}` : "no policy on file"} tone={!member.is_insured || member.team_status_reason === "insurance_expired" ? "bad" : member.insurance_expiring_soon ? "warn" : "ok"} />
		<Clock label="EFR / CPR" status={member.efr_expiring_soon ? "Renew soon" : "Current"} detail={`exp ${fmtShortDate(member.efr_expiry)}`} tone={member.team_status_reason === "efr_expired" ? "bad" : member.efr_expiring_soon ? "warn" : "ok"} />
	  </div>

	  <div className="team-card-foot">
		<div className="team-language-tags">
		{languages.length > 0 ? (
		  languages.map((lang) => <span key={lang} className="team-language-tag">{lang}</span>)
		) : (
		  <span className="team-language-tag">EN</span>
		)}
		</div>
		<button className="team-edit-btn" onClick={onEdit}>Edit profile</button>
	  </div>
	</article>
  );
}

function TeamRow({ member, onEdit }: { member: TeamMember; onEdit: () => void }) {
  const name = memberName(member);
  const bioLine = compactBio(member, 90);

  return (
	<article className={member.is_assignable ? "team-row" : "team-row team-row-attention"}>
	  <MemberAvatar member={member} name={name} className="team-row-avatar" />
	  <div className="team-row-id">
		<div className="team-row-name">{name}</div>
		<div className="team-row-meta">
		  {member.agency ? `${member.agency} ` : ""}
		  {certLine(member)}
		  {localProfessionalLine(member) ? ` · Local pro: ${localProfessionalLine(member)}` : ""}
		  {bioLine ? (
			<>
			  <br />
			  {bioLine}
			</>
		  ) : null}
		</div>
	  </div>
	  <span className={isInstructor(member) ? "team-row-role team-role-instructor" : "team-row-role team-role-guide"}>
		{isInstructor(member) ? "INSTR" : "GUIDE"}
	  </span>
	  <MiniClock label="Member" value={fmtShortDate(member.membership_expiry)} />
	  <MiniClock label="Insurance" value={member.is_insured ? "Insured" : "Not insured"} bad={!member.is_insured} />
	  <MiniClock label="EFR" value={fmtShortDate(member.efr_expiry)} />
	  <StatusPill member={member} />
	  <button className="team-edit-btn" onClick={onEdit}>Edit</button>
	</article>
  );
}

function StatusPill({ member }: { member: TeamMember }) {
  if (member.is_assignable) return <span className="team-status-pill team-status-ready">✓ Assignable</span>;
  return <span className="team-status-pill team-status-attention">⚠ {labelize(member.team_status_reason)}</span>;
}

function Clock(props: { label: string; status: string; detail: string; tone: "ok" | "warn" | "bad" }) {
  return (
	<div className={`team-clock team-clock-${props.tone}`}>
	  <div className="team-clock-label">{props.label}</div>
	  <div className="team-clock-status">{props.status}</div>
	  <div className="team-clock-detail">{props.detail}</div>
	</div>
  );
}

function MiniClock(props: { label: string; value: string; bad?: boolean }) {
  return (
	<div className="team-mini-clock">
	  <div className="team-clock-label">{props.label}</div>
	  <div className={props.bad ? "team-mini-clock-value team-mini-clock-bad" : "team-mini-clock-value"}>{props.value}</div>
	</div>
  );
}

function AddTeamModal({
  mode,
  member,
  onClose,
  onSaved,
}: {
  mode: "add" | "edit";
  member?: TeamMember;
  onClose: () => void;
  onSaved: () => void;
}) {
  const name = memberName(member);
  const modalInitials = mode === "edit" && name ? initials(name) : "+";

  const [fullName, setFullName] = useState(mode === "edit" ? name : "");
  const [email, setEmail] = useState("");
  const [saveError, setSaveError] = useState("");
  const [archiveConfirm, setArchiveConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [role, setRole] = useState<RoleMode>(member && isInstructor(member) ? "instructor" : "guide");
  const [agency, setAgency] = useState(member?.agency || "PADI");
  const [certificationLevel, setCertificationLevel] = useState(member?.certification_level || "");
  const [memberNumber, setMemberNumber] = useState(member?.member_number || "");
  const [membershipExpiry, setMembershipExpiry] = useState(member?.membership_expiry ? fmtDate(member.membership_expiry) : "");
  const [localProfessionalAuthority, setLocalProfessionalAuthority] = useState(member?.local_professional_authority || "CDWS");
  const [localProfessionalCategory, setLocalProfessionalCategory] = useState(member?.local_professional_category || "");
  const [localProfessionalIdNumber, setLocalProfessionalIdNumber] = useState(member?.local_professional_id_number || "");
  const [localProfessionalExpiry, setLocalProfessionalExpiry] = useState(member?.local_professional_expiry ? fmtDate(member.local_professional_expiry) : "");
  const [isInsured, setIsInsured] = useState(Boolean(member?.is_insured));
  const [insuranceCarrier, setInsuranceCarrier] = useState(member?.insurance_carrier || "");
  const [insurancePolicyNo, setInsurancePolicyNo] = useState(member?.insurance_policy_no || "");
  const [insuranceExpiry, setInsuranceExpiry] = useState(member?.insurance_expiry ? fmtDate(member.insurance_expiry) : "");
  const [efrExpiry, setEfrExpiry] = useState(member?.efr_expiry ? fmtDate(member.efr_expiry) : "");
  const [languages, setLanguages] = useState(member?.languages_spoken || "EN, AR");
  const [yearsExperience, setYearsExperience] = useState(String(member?.years_experience || ""));
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(member?.profile_photo_url || "");
  const [bio, setBio] = useState(member?.bio || "");
  const [photoUploadStatus, setPhotoUploadStatus] = useState("");

  const today = new Date().toISOString().slice(0, 10);
  const membershipOk = Boolean(membershipExpiry) && membershipExpiry >= today;
  const insuranceOk = isInsured && Boolean(insuranceExpiry) && insuranceExpiry >= today;
  const efrOk = Boolean(efrExpiry) && efrExpiry >= today;
  const localProfessionalOk = Boolean(localProfessionalAuthority.trim()) && Boolean(localProfessionalCategory.trim()) && Boolean(localProfessionalIdNumber.trim()) && Boolean(localProfessionalExpiry) && localProfessionalExpiry >= today;
  const previewAssignable = Boolean(fullName.trim()) && membershipOk && localProfessionalOk && insuranceOk && efrOk;

  let previewReason = "Active, current, insured, EFR in date.";
  if (!fullName.trim()) previewReason = "Full name required.";
  else if (!membershipOk) previewReason = "Membership expiry missing or lapsed.";
  else if (!localProfessionalOk) previewReason = "Local professional credential missing or expired.";
  else if (!insuranceOk) previewReason = "Insurance missing or expired.";
  else if (!efrOk) previewReason = "EFR / CPR missing or expired.";

  async function saveEdit() {
	if (isSaving || mode !== "edit" || member?.leader_id == null) return;
	setSaveError("");
	setIsSaving(true);

	try {
	  const response = await apiFetch(
		`/api/v1/dashboard/team/${member.leader_id}`,
		{
		  method: "PATCH",
		  headers: { "Content-Type": "application/json" },
		  body: JSON.stringify({
			role,
			certification_level: certificationLevel.trim() || null,
			agency: agency.trim() || null,
			member_number: memberNumber.trim() || null,
			membership_expiry: membershipExpiry || null,
			local_professional_authority: localProfessionalAuthority.trim() || null,
			local_professional_category: localProfessionalCategory.trim() || null,
			local_professional_id_number: localProfessionalIdNumber.trim() || null,
			local_professional_expiry: localProfessionalExpiry || null,
			is_insured: isInsured,
			insurance_carrier: insuranceCarrier.trim() || null,
			insurance_policy_no: insurancePolicyNo.trim() || null,
			insurance_expiry: insuranceExpiry || null,
			efr_expiry: efrExpiry || null,
			languages_spoken: languages.trim() || null,
			years_experience: yearsExperience.trim() || null,
			profile_photo_url: profilePhotoUrl.trim() || null,
			bio: bio.trim() || null,
		  }),
		}
	  );
	  const json = await response.json().catch(() => ({}));

	  if (!response.ok || json?.ok === false) {
		const message = json?.message || `HTTP ${response.status}`;
		setSaveError(message);
		console.error("[Team] Edit failed:", message);
		return;
	  }
	} finally {
	  setIsSaving(false);
	}

	onSaved();
  }

  async function saveAdd() {
	if (isSaving) return;
	setSaveError("");
	setIsSaving(true);

	try {
	  const response = await apiFetch("/api/v1/dashboard/team", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({
		  full_name: fullName,
		  email,
		  role,
		  certification_level: certificationLevel.trim() || null,
		  agency: agency.trim() || null,
		  member_number: memberNumber.trim() || null,
		  membership_expiry: membershipExpiry || null,
		  local_professional_authority: localProfessionalAuthority.trim() || null,
		  local_professional_category: localProfessionalCategory.trim() || null,
		  local_professional_id_number: localProfessionalIdNumber.trim() || null,
		  local_professional_expiry: localProfessionalExpiry || null,
		  is_insured: isInsured,
		  insurance_carrier: insuranceCarrier.trim() || null,
		  insurance_policy_no: insurancePolicyNo.trim() || null,
		  insurance_expiry: insuranceExpiry || null,
		  efr_expiry: efrExpiry || null,
		  languages_spoken: languages.trim() || null,
		  years_experience: yearsExperience.trim() || null,
		  profile_photo_url: profilePhotoUrl.trim() || null,
		  bio: bio.trim() || null,
		}),
	  });
	  const json = await response.json().catch(() => ({}));

	  if (response.status === 400 || response.status === 409) {
		setSaveError(
		  json?.message ||
			(json?.field === "full_name"
			  ? "Full name is required."
			  : "Email is required.")
		);
		return;
	  }

	  if (!response.ok || json?.ok === false) {
		console.error("[Team] Add failed:", json?.message || `HTTP ${response.status}`);
		return;
	  }
	} finally {
	  setIsSaving(false);
	}

	onSaved();
  }

  async function uploadProfilePhoto(file: File | null) {
	if (!file || mode !== "edit" || member?.leader_id == null || isSaving) return;
	setSaveError("");
	setPhotoUploadStatus("Uploading photo...");
	setIsSaving(true);

	try {
	  const formData = new FormData();
	  formData.append("image", file);

	  const response = await apiFetch(
		`/api/v1/dashboard/team/${member.leader_id}/profile-photo`,
		{
		  method: "POST",
		  body: formData,
		}
	  );
	  const json = await response.json().catch(() => ({}));

	  if (!response.ok || json?.ok === false) {
		const message = json?.message || `HTTP ${response.status}`;
		setSaveError(message);
		setPhotoUploadStatus("");
		console.error("[Team] Photo upload failed:", message);
		return;
	  }

	  setProfilePhotoUrl(json.profile_photo_url || "");
	  setPhotoUploadStatus("Photo uploaded");
	} finally {
	  setIsSaving(false);
	}
  }

  async function archiveMember() {
	if (isSaving || mode !== "edit" || member?.leader_id == null) return;
	setSaveError("");
	setIsSaving(true);

	try {
	  const response = await apiFetch(
		`/api/v1/dashboard/team/${member.leader_id}/archive`,
		{
		  method: "POST",
		}
	  );
	  const json = await response.json().catch(() => ({}));

	  if (!response.ok || json?.ok === false) {
		const message = json?.message || `HTTP ${response.status}`;
		setSaveError(message);
		console.error("[Team] Archive failed:", message);
		return;
	  }
	} finally {
	  setIsSaving(false);
	}

	onSaved();
  }

  return (
	<div className="team-modal-backdrop">
	  <div className="team-modal" role="dialog" aria-modal="true" aria-label="Team member">
		<div className="team-modal-head">
		  <MemberAvatar
			member={{ ...member, profile_photo_url: profilePhotoUrl } as TeamMember}
			name={name || fullName || modalInitials}
			className="team-modal-avatar"
		  />
		  <div>
			<h2>{mode === "edit" ? `Edit ${name || "team member"}` : "Add team member"}</h2>
			<p>{mode === "edit" ? "Update role, currency, and profile." : "Their professional currency decides whether they're assignable on Schedule."}</p>
		  </div>
		  <button className="team-modal-close" onClick={onClose} aria-label="Close">×</button>
		</div>

		<div className="team-modal-body">
		  <section className="team-form-panel">
			<div className="team-form-section-title">Identity & professional</div>
			<div className="team-form-grid">
			  <label className="team-field team-field-full">
				<span>Full name</span>
				<input
				  value={fullName}
				  onChange={(e) => setFullName(e.target.value)}
				  placeholder="e.g. Sindbad Mansour"
				  readOnly={mode === "edit"}
				/>
			  </label>

			  {mode === "add" ? (
				<label className="team-field team-field-full">
				  <span>Email</span>
				  <input
					type="email"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
				  />
				</label>
			  ) : null}

			  <div className="team-field">
				<span>Role</span>
				<div className="team-role-segment">
				  <button type="button" className={role === "guide" ? "is-active" : ""} onClick={() => setRole("guide")}>Guide</button>
				  <button type="button" className={role === "instructor" ? "is-active" : ""} onClick={() => setRole("instructor")}>Instructor</button>
				</div>
			  </div>

			  <label className="team-field">
				<span>Agency</span>
				<select value={agency} onChange={(e) => setAgency(e.target.value)}>
				  <option value="PADI">PADI</option>
				  <option value="SSI">SSI</option>
				  <option value="TDI/SDI">TDI/SDI</option>
				  <option value="NAUI">NAUI</option>
				  <option value="Other">Other</option>
				</select>
			  </label>

			  <label className="team-field">
				<span>Certification level</span>
				<input value={certificationLevel} onChange={(e) => setCertificationLevel(e.target.value)} placeholder="e.g. Divemaster" />
			  </label>

			  <label className="team-field">
				<span>Member / pro number</span>
				<input value={memberNumber} onChange={(e) => setMemberNumber(e.target.value)} placeholder="#" />
			  </label>

			</div>
		  </section>

		  <section className="team-form-panel">

			  <div className="team-form-section-title">Local Professional Identification & Certification</div>

				<div className="team-form-grid">
					
					<label className="team-field">
						<span>Local authority</span>
						<input value={localProfessionalAuthority} onChange={(e) => setLocalProfessionalAuthority(e.target.value)} placeholder="e.g. CDWS" />
					</label>

					<label className="team-field">
						<span>CDWS Professional Category</span>
						<input value={localProfessionalCategory} onChange={(e) => setLocalProfessionalCategory(e.target.value)} placeholder="e.g. Dive Instructor" />
					</label>

					<label className="team-field">
						<span>CDWS Professional ID Number</span>
						<input value={localProfessionalIdNumber} onChange={(e) => setLocalProfessionalIdNumber(e.target.value)} placeholder="CDWS #" />
					</label>

					<label className="team-field">
						<span>Local professional expiry</span>
						<input type="date" value={localProfessionalExpiry} onChange={(e) => setLocalProfessionalExpiry(e.target.value)} />
					</label>

				</div>

		  </section>

		  {saveError ? <div className="team-error">{saveError}</div> : null}

		  <section className="team-form-panel">
			<div className="team-form-section-title">Professional currency — the three clocks</div>
			<div className="team-form-grid">
			  <label className="team-field team-field-full">
				<span>Membership / teaching status — expiry</span>
				<input type="date" value={membershipExpiry} onChange={(e) => setMembershipExpiry(e.target.value)} />
			  </label>

			  <div className="team-field team-field-full">
				<div className="team-toggle-row">
				  <div>
					<strong>Carries professional liability insurance</strong>
					<span>Individual policy — required to guide / teach in this region.</span>
				  </div>
				  <button type="button" className={isInsured ? "team-switch is-on" : "team-switch"} onClick={() => setIsInsured(!isInsured)}>
					<span />
				  </button>
				</div>
			  </div>

			  {isInsured ? (
				<>
				  <label className="team-field">
					<span>Carrier</span>
					<input value={insuranceCarrier} onChange={(e) => setInsuranceCarrier(e.target.value)} placeholder="e.g. DAN" />
				  </label>
				  <label className="team-field">
					<span>Policy number</span>
					<input value={insurancePolicyNo} onChange={(e) => setInsurancePolicyNo(e.target.value)} placeholder="#" />
				  </label>
				  <label className="team-field">
					<span>Insurance — expiry</span>
					<input type="date" value={insuranceExpiry} onChange={(e) => setInsuranceExpiry(e.target.value)} />
				  </label>
				</>
			  ) : null}

			  <label className="team-field">
				<span>EFR / CPR currency — expiry</span>
				<input type="date" value={efrExpiry} onChange={(e) => setEfrExpiry(e.target.value)} />
			  </label>
			</div>
		  </section>

		  <div className={previewAssignable ? "team-assign-preview team-assign-preview-ok" : "team-assign-preview team-assign-preview-no"}>
			<strong>{previewAssignable ? "✓ Assignable" : "Not assignable"}</strong>
			<span>{previewReason}</span>
		  </div>

		  <section className="team-form-panel">
			<div className="team-form-section-title">Work eligibility · record only — does not affect assignability</div>
			<div className="team-form-grid">
			  <label className="team-field">
				<span>Residency</span>
				<input disabled placeholder="Local / foreign national" />
			  </label>
			  <label className="team-field">
				<span>ID document</span>
				<input disabled placeholder="Masked record only" />
			  </label>
			  <label className="team-field">
				<span>Work permit status</span>
				<input disabled placeholder="On file / receipt pending / not required" />
			  </label>
			  <label className="team-field">
				<span>Permit / expiry</span>
				<input disabled placeholder="Record only" />
			  </label>
			</div>
			<p className="team-field-note">
			  Restricted — operator & instructor tiers only, masked, never cross-operator or consumer-visible. No status is shown on the roster. These fields are not yet stored.
			</p>
		  </section>

		  <section className="team-form-panel">
			<div className="team-form-section-title">Profile — website & team</div>
			<div className="team-form-grid">
			  <label className="team-field">
				<span>Languages</span>
				<input value={languages} onChange={(e) => setLanguages(e.target.value)} placeholder="EN, AR" />
			  </label>
			  <label className="team-field">
				<span>Years experience</span>
				<input value={yearsExperience} onChange={(e) => setYearsExperience(e.target.value)} placeholder="0" />
			  </label>
			  <label className="team-field team-field-full">
				<span>Profile photo</span>
				<input
				  type="file"
				  accept="image/jpeg,image/png,image/webp"
				  disabled={mode !== "edit" || isSaving}
				  onChange={(event) => uploadProfilePhoto(event.target.files?.[0] || null)}
				/>
			  </label>
			  {profilePhotoUrl ? (
				<label className="team-field team-field-full">
				  <span>Stored photo URL</span>
				  <input value={profilePhotoUrl} readOnly />
				</label>
			  ) : null}
			  {mode !== "edit" ? (
				<p className="team-field-note team-field-full">Save the member first, then edit their profile to upload a photo.</p>
			  ) : null}
			  {photoUploadStatus ? <p className="team-field-note team-field-full">{photoUploadStatus}</p> : null}
			  <label className="team-field team-field-full">
				<span>Short bio</span>
				<textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="A short public-facing profile for guests and the team." rows={4} />
			  </label>
			</div>
		  </section>
		</div>

		<div className="team-modal-foot">
		  {mode === "edit" && archiveConfirm ? (
			<>
			  <span>The member is removed from the roster and from assignment, and the record is kept.</span>
			  <button className="team-cancel-btn" onClick={() => setArchiveConfirm(false)}>
				Keep member
			  </button>
			  <button
				className="team-cancel-btn team-archive-btn"
				onClick={archiveMember}
				disabled={isSaving}
			  >
				Confirm archive
			  </button>
			</>
		  ) : (
			<>
			  <button className="team-cancel-btn" onClick={onClose}>Cancel</button>
			  {mode === "edit" ? (
				<button
				  className="team-cancel-btn team-archive-btn"
				  onClick={() => setArchiveConfirm(true)}
				>
				  Archive
				</button>
			  ) : null}
			  <button
				className="team-save-btn"
				onClick={mode === "edit" ? saveEdit : saveAdd}
				disabled={isSaving}
			  >
				{isSaving ? "Saving..." : mode === "edit" ? "Save changes" : "Add to team"}
			  </button>
			</>
		  )}
		</div>
	  </div>
	</div>
  );
}
